import { useEffect, useState, useRef } from 'react';
import { api } from '@/api';

interface AdminInvoice {
  id: string;
  userId: string;
  user: { firstName: string; lastName: string; email: string };
  invoiceNumber: string;
  period: string;
  totalHours: number;
  totalAmount: number;
  status: string;
  pdfAvailable: boolean;
  generatedAt: string;
  emailedAt: string | null;
}

const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
function periodLabel(period: string): string {
  const [y, m] = period.split('-');
  return `${monthNames[parseInt(m,10)-1]} ${y}`;
}
function fmtMoney(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function defaultPrevMonth(): string {
  const d = new Date();
  d.setUTCDate(15);
  d.setUTCMonth(d.getUTCMonth() - 1);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export default function AdminInvoicesPage() {
  const [invoices, setInvoices] = useState<AdminInvoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [genPeriod, setGenPeriod] = useState(defaultPrevMonth());
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const uploadLogo = async (file: File) => {
    if (!file.type.includes('png')) {
      setError('Logo must be a PNG file');
      return;
    }
    setIsUploadingLogo(true);
    setError('');
    try {
      const token = localStorage.getItem('accessToken');
      const apiBase = import.meta.env.PROD
        ? 'https://ultisim-teams-api.ultisim.workers.dev/api/v1'
        : '/api/v1';
      const res = await fetch(`${apiBase}/invoices/upload-logo`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'image/png',
        },
        body: file,
      });
      const data = await res.json() as { success?: boolean; error?: { message?: string }; data?: { sizeBytes?: number } };
      if (!res.ok || !data.success) {
        throw new Error(data.error?.message || 'Upload failed');
      }
      setSuccessMsg(`Logo uploaded (${Math.round((data.data?.sizeBytes || 0) / 1024)} KB). New invoices will use the logo. Re-generate any existing invoices to update their PDFs.`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to upload logo');
    } finally {
      setIsUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const load = async () => {
    setIsLoading(true);
    try {
      const data = await api.get<AdminInvoice[]>('/invoices', { all: 'true' });
      setInvoices(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load invoices');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (successMsg) { const t = setTimeout(() => setSuccessMsg(''), 5000); return () => clearTimeout(t); }
  }, [successMsg]);

  const generateForPeriod = async () => {
    if (!/^\d{4}-\d{2}$/.test(genPeriod)) {
      setError('Period must be YYYY-MM');
      return;
    }
    setIsGenerating(true);
    setError('');
    try {
      const result = await api.post<{ generated: number; totalUsers: number; errors: string[] }>(
        '/invoices/generate', { period: genPeriod }
      );
      setSuccessMsg(`Generated ${result.generated} of ${result.totalUsers} invoices for ${periodLabel(genPeriod)}${result.errors.length ? `. Errors: ${result.errors.join('; ')}` : ''}`);
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to generate');
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadPdf = async (id: string, filename: string) => {
    const token = localStorage.getItem('accessToken');
    const res = await fetch(`${import.meta.env.PROD ? 'https://ultisim-teams-api.ultisim.workers.dev/api/v1' : '/api/v1'}/invoices/${id}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) { setError('Failed to download PDF'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const resendEmail = async (id: string) => {
    try {
      await api.post(`/invoices/${id}/resend-email`);
      setSuccessMsg('Email re-sent');
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to resend');
    }
  };

  if (isLoading) return <div className="flex items-center justify-center h-64"><div className="spinner w-8 h-8" /></div>;

  // Group invoices by period for nicer display
  const byPeriod = new Map<string, AdminInvoice[]>();
  for (const inv of invoices) {
    if (!byPeriod.has(inv.period)) byPeriod.set(inv.period, []);
    byPeriod.get(inv.period)!.push(inv);
  }
  const periods = Array.from(byPeriod.keys()).sort().reverse();

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Invoices</h1>
          <p className="text-dark-400 mt-1">Auto-generated on the 1st of each month. Use the form on the right to manually generate or re-generate a period.</p>
        </div>
        <div className="flex gap-2 items-end flex-wrap">
          <input
            ref={logoInputRef}
            type="file"
            accept="image/png"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(f); }}
          />
          <button
            onClick={() => logoInputRef.current?.click()}
            disabled={isUploadingLogo}
            className="btn-ghost"
            title="Upload the UltiSim logo PNG used on invoice PDFs"
          >
            {isUploadingLogo ? <><span className="spinner w-4 h-4" /> Uploading...</> : 'Upload Logo'}
          </button>
          <div>
            <label className="block text-xs text-dark-400 mb-1">Period (YYYY-MM)</label>
            <input
              type="text"
              value={genPeriod}
              onChange={(e) => setGenPeriod(e.target.value)}
              placeholder="2026-04"
              className="input text-sm py-1.5 w-32"
            />
          </div>
          <button onClick={generateForPeriod} disabled={isGenerating} className="btn-primary">
            {isGenerating ? <><span className="spinner w-4 h-4" /> Generating...</> : 'Generate Invoices'}
          </button>
        </div>
      </div>

      {successMsg && <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm">{successMsg}</div>}
      {error && <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">{error}</div>}

      {invoices.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-dark-400">No invoices generated yet. Use the form above to create the first batch.</p>
        </div>
      ) : (
        periods.map((p) => {
          const rows = byPeriod.get(p)!;
          const periodTotal = rows.reduce((s, r) => s + r.totalAmount, 0);
          const periodHours = rows.reduce((s, r) => s + r.totalHours, 0);
          return (
            <div key={p} className="card">
              <div className="flex items-center justify-between mb-3 pb-3 border-b border-dark-700">
                <h2 className="text-lg font-semibold text-white">{periodLabel(p)}</h2>
                <div className="text-sm text-dark-400">
                  <span className="mr-4">{rows.length} invoice{rows.length !== 1 ? 's' : ''}</span>
                  <span className="mr-4">{periodHours.toFixed(0)}h total</span>
                  <span className="text-white font-medium">{fmtMoney(periodTotal)}</span>
                </div>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="bg-dark-800/30">
                    <th className="text-left px-3 py-2 text-xs font-medium text-dark-400 uppercase tracking-wider">User</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-dark-400 uppercase tracking-wider">Invoice #</th>
                    <th className="text-right px-3 py-2 text-xs font-medium text-dark-400 uppercase tracking-wider">Hours</th>
                    <th className="text-right px-3 py-2 text-xs font-medium text-dark-400 uppercase tracking-wider">Amount</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-dark-400 uppercase tracking-wider">Status</th>
                    <th className="text-right px-3 py-2 text-xs font-medium text-dark-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((inv) => (
                    <tr key={inv.id} className="border-t border-dark-800/50">
                      <td className="px-3 py-2 text-dark-200">{inv.user.firstName} {inv.user.lastName}</td>
                      <td className="px-3 py-2 text-dark-400 text-sm font-mono">{inv.invoiceNumber}</td>
                      <td className="px-3 py-2 text-right text-dark-200">{inv.totalHours.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right text-white">{fmtMoney(inv.totalAmount)}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${inv.status === 'emailed' ? 'bg-green-900/30 text-green-400' : inv.status === 'quickbooks_pushed' ? 'bg-blue-900/30 text-blue-400' : 'bg-dark-700 text-dark-300'}`}>
                          {inv.status === 'emailed' ? 'Emailed' : inv.status === 'quickbooks_pushed' ? 'In QuickBooks' : 'Issued'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right space-x-3">
                        {inv.pdfAvailable && <button onClick={() => downloadPdf(inv.id, `${inv.invoiceNumber}.pdf`)} className="text-primary-400 hover:text-primary-300 text-sm">PDF</button>}
                        <button onClick={() => resendEmail(inv.id)} className="text-primary-400 hover:text-primary-300 text-sm">Re-email</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })
      )}
    </div>
  );
}
